<template>
    <div>
        <div class="bg-white rounded-md shadow overflow-hidden max-w-3xl">
            <!-- Inactive FAQ banner -->
            <div v-if="!faq.status"
                class="bg-yellow-100 text-yellow-800 p-3 px-8 border-b border-yellow-200 flex items-center">
                <icon name="exclamation" class="w-5 h-5 mr-2" />
                <span>{{ __('This FAQ is currently inactive and will not be visible to users.') }}</span>
            </div>

            <form @submit.prevent="update">
                <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
                    <text-input v-model="form.name" :error="form.errors.name" class="pr-6 pb-8 w-full"
                        :label="__('Name')" placeholder="Enter name (max 50 characters)" :maxlength="50"
                        :disabled="!user_access.faq.update || (auth.user.role.slug === 'agent')" />

                    <select-input-filter v-if="user_access.faq.update && auth.user.role.slug !== 'agent'"
                        placeholder="Start typing to search categories" :items="categories" v-model="form.category_id"
                        :error="form.errors.category_id" class="pr-6 pb-8 w-full lg:w-1/2" :label="__('Category')"
                        :disabled="!user_access.faq.update || (auth.user.role.slug === 'agent')" />

                    <!-- Readonly category display for agents -->
                    <div v-else class="pr-6 pb-8 w-full lg:w-1/2 flex flex-col">
                        <label class="form-label">{{ __('Category') }}</label>
                        <div class="mt-1 p-2 border border-gray-300 rounded bg-gray-50">
                            {{ faq.category?.name || __('Uncategorized') }}
                        </div>
                    </div>

                    <checkbox-input v-model="form.status" :error="form.errors.status" class="pr-6 pb-8 w-full lg:w-1/2"
                        :label="__('Active')"
                        :disabled="!user_access.faq.update || !['admin', 'manager'].includes(auth.user.role.slug)" />

                    <textarea-input v-model="form.details" :error="form.errors.details" class="pr-6 pb-8 w-full"
                        :label="__('Details')"
                        :disabled="!user_access.faq.update || (auth.user.role.slug === 'agent')" />

                    <!-- Created/Updated info (readonly) -->
                    <div class="pr-6 pb-8 w-full lg:w-1/2 flex flex-col">
                        <label class="form-label text-sm font-medium text-gray-700">{{ __('Created') }}</label>
                        <div class="mt-1 text-sm text-gray-600">
                            {{ formatDate(faq.created_at) }}
                            <span v-if="faq.created_by"> by {{ faq.created_by.name }}</span>
                        </div>
                    </div>

                    <div class="pr-6 pb-8 w-full lg:w-1/2 flex flex-col">
                        <label class="form-label text-sm font-medium text-gray-700">{{ __('Last Updated') }}</label>
                        <div class="mt-1 text-sm text-gray-600">
                            {{ formatDate(faq.updated_at) }}
                            <span v-if="faq.updated_by"> by {{ faq.updated_by.name }}</span>
                        </div>
                    </div>
                </div>

                <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center space-x-2">
                    <div>
                        <!-- Delete button - only for admins with delete permission -->
                        <button v-if="user_access.faq.delete && auth.user.role.slug === 'admin'"
                            class="text-red-600 hover:underline" type="button" @click="destroy"
                            :disabled="!user_access.faq.update">
                            {{ __('Delete FAQ') }}
                        </button>
                    </div>

                    <div class="flex items-center space-x-2">
                        <Link class="btn-gray" :href="route('faqs.index')" as="button" type="button" preserve-scroll>
                        {{ __('Cancel') }}
                        </Link>

                        <loading-button v-if="user_access.faq.update" :loading="form.processing" class="btn-indigo"
                            type="submit"
                            :disabled="!user_access.faq.update || (auth.user.role.slug === 'agent' && faq.status === false)">
                            {{ form.processing ? __('Updating...') : __('Update FAQ') }}
                        </loading-button>

                        <!-- Show message if user doesn't have permission -->
                        <div v-else class="text-red-600 text-sm bg-red-50 px-4 py-2 rounded border border-red-200">
                            {{ __('You do not have permission to update FAQs') }}
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInputFilter from '@/Shared/SelectInputFilter'
import TextareaInput from '@/Shared/TextareaInput'
import CheckboxInput from '@/Shared/CheckboxInput'
import LoadingButton from '@/Shared/LoadingButton'
import Icon from '@/Shared/Icon'
import moment from 'moment'

export default {
    metaInfo() {
        return { title: `Edit FAQ: ${this.faq.name}` }
    },
    components: {
        LoadingButton,
        CheckboxInput,
        TextareaInput,
        SelectInputFilter,
        TextInput,
        Link,
        Icon,
    },
    layout: Layout,
    props: {
        faq: Object,
        categories: Array,
        auth: Object,
    },
    data() {
        return {
            form: this.$inertia.form({
                name: this.faq.name,
                details: this.faq.details,
                status: this.faq.status,
                category_id: this.faq.category_id,
            }),
            user_access: this.$page.props.auth.user.access || {},
        }
    },
    methods: {
        formatDate(date) {
            return moment(date).format('MMM D, YYYY h:mm A')
        },

        update() {
            if (!this.user_access.faq.update) {
                this.showToast('error', 'You do not have permission to update FAQs')
                return
            }

            // Prevent agents from activating inactive FAQs
            if (this.auth.user.role.slug === 'agent' && !this.faq.status) {
                this.showToast('error', 'You cannot update an inactive FAQ')
                return
            }

            this.form.put(route('faqs.update', this.faq.id), {
                preserveScroll: true,
                onSuccess: () => {
                    this.showToast('success', 'FAQ updated successfully!')
                },
                onError: (errors) => {
                    this.showToast('error', 'Failed to update FAQ')
                }
            })
        },

        destroy() {
            if (!this.user_access.faq.delete) {
                this.showToast('error', 'You do not have permission to delete FAQs')
                return
            }

            if (confirm('Are you sure you want to delete this FAQ? This action cannot be undone.')) {
                this.$inertia.delete(route('faqs.destroy', this.faq.id), {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.showToast('success', 'FAQ deleted successfully!')
                        this.$inertia.visit(route('faqs.index'))
                    },
                    onError: () => {
                        this.showToast('error', 'Failed to delete FAQ')
                    }
                })
            }
        },

        showToast(type, message) {
            // Replace with your actual toast implementation
            // Example: this.$toast[type](message)
            alert(`${type}: ${message}`)
        }
    },
}
</script>
